import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Clock, Mail, MapPin, Phone } from 'lucide-react'
import { fetchContent, framing, has, paragraphs, text, publicHref } from '../api/content'
import { fetchCatalog } from '../api/catalog'
import StudioImage, { backgroundFraming } from './StudioImage'
import FacebookFeed from './FacebookFeed'
import { StudioAddress } from './StudioAddress'

/**
 * The studio's homepage (M13, D-32).
 *
 * **Every word comes from ContentBlock storage.** Tonya can already edit her
 * own site on Wix; replacing that with markup only a developer can change would
 * be a regression, and the first stale sentence would become a phone call
 * (A-16, NFR-14).
 *
 * Sections render only when their content exists, so a block she empties
 * disappears cleanly rather than leaving a heading over nothing.
 */
export default function HomeView() {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  // Just for the "N classes running" line — the homepage should not pretend to
  // be the catalog.
  const { data: catalog } = useQuery({ queryKey: ['catalog', {}], queryFn: () => fetchCatalog() })

  const gallery = [1, 2, 3]
    .map((n) => ({
      image: text(content, `home.gallery${n}.image`),
      caption: text(content, `home.gallery${n}.caption`),
      framing: framing(content, `home.gallery${n}.image`),
    }))
    .filter((photo) => photo.image)

  const points = [1, 2, 3]
    .map((n) => ({
      title: text(content, `home.point${n}.title`),
      body: text(content, `home.point${n}.body`),
    }))
    .filter((p) => p.title || p.body)

  return (
    <div>
      {/* ---- Hero ---- */}
      <section className="relative overflow-hidden border-b border-line bg-accent-soft">
        {/*
          The studio's own photograph when there is one. Dimmed behind the
          words rather than beside them, because a dance photo is the whole
          argument for a dance studio and a headline over it says more than
          either alone. Falls back to flat colour, never to a broken frame.

          Framed like every other photograph, which it was not until D-253. It
          stays a CSS background — the dimming and the text over it are what
          the element is for — so it cannot read `object-position`;
          `background-position` is the same idea and takes the same two
          numbers. The section clips, because a zoomed background layer paints
          outside its own box.

          It was the one placement on the site that ignored framing, and the
          awkward part is that `content:home.hero.image` is the worked example
          inside D-199's own rationale.
        */}
        {has(content, 'home.hero.image') && (
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-cover opacity-25"
            style={{
              backgroundImage: `url(${text(content, 'home.hero.image')})`,
              ...backgroundFraming(framing(content, 'home.hero.image')),
            }}
          />
        )}

        <div className="relative mx-auto max-w-[56rem] px-5 py-16 text-center">
          <h1 className="font-display text-4xl leading-tight text-balance sm:text-5xl">
            {text(content, 'home.hero.headline', 'Where Cabot Dances')}
          </h1>

          {has(content, 'home.hero.subhead') && (
            <p className="mx-auto mt-4 max-w-[36rem] text-[16px] leading-relaxed text-ink-2">
              {text(content, 'home.hero.subhead')}
            </p>
          )}

          {/*
            One call to action, not two. Registering starts by choosing a class
            (D-111) — a second button labelled "Register" would send a parent to
            a Jackrabbit form with nothing selected, which is the version of
            this that generates a phone call.
          */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to={publicHref('/classes')}
              className="rounded-lg bg-accent px-5 py-2.5 text-[14px] font-semibold text-on-accent no-underline hover:brightness-110"
            >
              See this season&rsquo;s classes
            </Link>
          </div>

          {has(content, 'home.season') && (
            <p className="mt-5 text-[13.5px] text-ink-3">
              {text(content, 'home.season')}
              {catalog && catalog.classes.length > 0 && (
                <> &middot; {catalog.classes.length} classes running</>
              )}
            </p>
          )}
        </div>
      </section>

      {/* ---- Welcome ---- */}
      {has(content, 'home.welcome.body') && (
        <section className="mx-auto flex max-w-[44rem] flex-col gap-4 px-5 py-14">
          {/* Blank-line separated, so a second paragraph is a paragraph rather
              than a run-on (D-240). */}
          {paragraphs(content, 'home.welcome.body').map((paragraph, i) => (
            <p key={i} className="text-[17px] leading-relaxed text-ink-2">
              {paragraph}
            </p>
          ))}
        </section>
      )}

      {/* ---- Why this studio ---- */}
      {points.length > 0 && (
        <section className="border-y border-line bg-surface-2">
          <div className="mx-auto grid max-w-[56rem] gap-8 px-5 py-14 sm:grid-cols-3">
            {points.map((point) => (
              <div key={point.title}>
                <h2 className="font-display text-lg text-balance">{point.title}</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-ink-2">{point.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- The studio's own photographs ---- */}
      {gallery.length > 0 && (
        <section className="border-y border-line bg-surface-2">
          <div className="mx-auto max-w-[56rem] px-5 py-14">
            <div className="grid gap-4 sm:grid-cols-3">
              {gallery.map((photo) => (
                <figure key={photo.image} className="min-w-0">
                  {/*
                    Fixed aspect with object-cover, so three portrait and
                    landscape shots line up instead of stair-stepping. Lazy,
                    because these are the studio's full-resolution originals
                    and a phone should not fetch all three before the text.
                  */}
                  <StudioImage
                    src={photo.image}
                    alt={photo.caption || 'Cabot Dance Academy'}
                    framing={photo.framing}
                    className="aspect-[4/5] w-full rounded-2xl border border-line"
                  />
                  {photo.caption && (
                    <figcaption className="mt-2 text-center text-[12.5px] text-ink-3">
                      {photo.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/*
        ---- What's happening now ----
        Straight after the studio's own photographs, and the pairing is the
        point: those say who the studio is, this says what it did this week.
        Alternates back to the plain surface so the two image-heavy bands do
        not merge into one (D-130).
      */}
      <FacebookFeed content={content} />

      {/* ---- Practicalities ---- */}
      <section className="mx-auto max-w-[56rem] px-5 py-14">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-4">
            {has(content, 'studio.hoursDetail') && (
              <Detail icon={<Clock size={16} strokeWidth={2} />} label="Studio hours">
                {text(content, 'studio.hoursDetail')}
              </Detail>
            )}

            {has(content, 'studio.address') && (
              <Detail icon={<MapPin size={16} strokeWidth={2} />} label="Where we are">
                {/*
                  Linked, as the phone and email beside it are (D-499) — the
                  address was the one detail here a visitor had to retype.

                  Not styled identically to them: it carries padding, to clear
                  the 24px the layout audit wants of anything tappable, and an
                  underline on hover. Those two are bare `text-accent-2` and are
                  themselves tap-target findings, so matching them exactly would
                  have meant copying the fault.
                */}
                <StudioAddress address={text(content, 'studio.address')} />
              </Detail>
            )}
          </div>

          <div className="flex flex-col gap-4">
            {has(content, 'studio.phone') && (
              <Detail icon={<Phone size={16} strokeWidth={2} />} label="Call us">
                <a href={`tel:${text(content, 'studio.phone').replace(/[^\d+]/g, '')}`} className="text-accent-2">
                  {text(content, 'studio.phone')}
                </a>
              </Detail>
            )}

            {has(content, 'studio.email') && (
              <Detail icon={<Mail size={16} strokeWidth={2} />} label="Email us">
                <a href={`mailto:${text(content, 'studio.email')}`} className="wrap-anywhere text-accent-2">
                  {text(content, 'studio.email')}
                </a>
              </Detail>
            )}
          </div>
        </div>
      </section>

      {/* ---- Closing invitation ---- */}
      {has(content, 'home.cta.body') && (
        <section className="border-t border-line bg-accent-soft">
          <div className="mx-auto max-w-[44rem] px-5 py-14 text-center">
            <p className="text-[16px] leading-relaxed text-ink-2">
              {text(content, 'home.cta.body')}
            </p>

            <Link
              to={publicHref('/classes')}
              className="mt-6 inline-block rounded-lg bg-accent px-5 py-2.5 text-[14px] font-semibold text-on-accent no-underline hover:brightness-110"
            >
              Find a class
            </Link>

            {has(content, 'home.tagline') && (
              <p className="mt-8 font-display text-lg text-accent-2">
                {text(content, 'home.tagline')}
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

function Detail({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 shrink-0 text-ink-3">{icon}</span>
      <div>
        <p className="text-[11px] font-bold tracking-[0.1em] text-ink-3 uppercase">{label}</p>
        <p className="mt-1 text-[14px] leading-relaxed text-ink-2">{children}</p>
      </div>
    </div>
  )
}
